import { useEffect, useState } from 'react';

import { Flex, Grid, Text } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { OnChangeValue } from 'react-select';

import { useAppSelector } from '@/store/hooks';
import { getIsPractitioner } from '@/store/selectors/transmissionReportSelectors';
import { useGetOrgByIdQuery } from '@/store/slices/api/organizationApiSlice';
import { useUpdatePrimaryDiagnosisMutation } from '@/store/slices/api/patientDiagnosesApiSlice';
import {
  useGetReportPatientDetailsQuery,
  useUpdatePatientMutation
} from '@/store/slices/api/transmissionReport/reportsPatientApiSlice';
import { formatUtcDate } from '@/utils/formatDate';

import DiagnosisSelect, {
  LabelAndValue
} from '@/pages/physician/ReviewTransmissionReportsPage/TransmissionReportDetails/Inputs/DiagnosisSelect';
import DxCodeSelectionModal from '@/pages/physician/TransmissionReportPage/ReportPatientDetails/MedicalInfoDetails/DxCodeSelectionModal';
import { oacOptions } from '@/types/oacList';
import { Patient } from '@/types/patient';
import Dropdown from '@/ui/Dropdown';
import LabeledField from '@/ui/LabeledField';
import SkeletonGrid from '@/ui/Skeleton';

interface MedicalInfoDetailsProps {
  patientId: string;
}

type EditableField = 'diagnosisOption' | 'oac';

const MedicalInfoDetails = ({ patientId }: MedicalInfoDetailsProps) => {
  const [editingField, setEditingField] = useState<EditableField | null>(null);
  const [isDxModalOpen, setIsDxModalOpen] = useState(false);
  const {
    data: patientResponse,
    isLoading: isPatientLoading,
    isError: isPatientError
  } = useGetReportPatientDetailsQuery(patientId, {
    skip: !patientId
  });
  const patientDetails = patientResponse?.patient;

  const { data: clinicData, isLoading: isClinicDataLoading } = useGetOrgByIdQuery(
    patientDetails?.clinic?.id ?? '',
    {
      skip: !patientDetails?.clinic?.id
    }
  );

  const isNewDiagnosisModalEnabled =
    clinicData?.organization?.parent?.enableMultipleDiagnosesBilling ??
    clinicData?.organization?.enableMultipleDiagnosesBilling ??
    false;

  const isEditing = (field: EditableField) => editingField === field;
  const handleEdit = (field: EditableField) => {
    if (field === 'diagnosisOption' && isNewDiagnosisModalEnabled) {
      setIsDxModalOpen(true);
    } else {
      setEditingField(field);
    }
  };

  const [updatePatientDetails] = useUpdatePatientMutation();
  const [updatePrimaryDiagnosis] = useUpdatePrimaryDiagnosisMutation();
  const isPractitioner = useAppSelector((state) => getIsPractitioner(state));

  const [loadingField, setLoadingField] = useState<EditableField | null>(null);
  const isFieldLoading = (field: EditableField) => loadingField === field;

  const [formValues, setFormValues] = useState<{
    diagnosisOption?: LabelAndValue;
    oac?: string;
    oacUpdatedAt?: string;
    oacUpdatedByUser?: string;
  }>({
    diagnosisOption: undefined,
    oac: undefined,
    oacUpdatedAt: undefined,
    oacUpdatedByUser: undefined
  });

  useEffect(() => {
    if (patientDetails?.icdDiagnosis) {
      const { icdDiagnosis } = patientDetails;
      setFormValues((prev) => ({
        ...prev,
        diagnosisOption: {
          label: `${icdDiagnosis.code} - ${icdDiagnosis.description}`,
          value: icdDiagnosis.code
        }
      }));
    }
    if (patientDetails?.oac) {
      const oacData = patientDetails;
      setFormValues((prev) => ({
        ...prev,
        oac: oacData?.oac,
        oacUpdatedAt: oacData?.oacUpdatedAt ? formatUtcDate(oacData?.oacUpdatedAt) : undefined,
        oacUpdatedByUser: oacData.oacUpdatedByUser
          ? `${oacData.oacUpdatedByUser?.family} ${oacData.oacUpdatedByUser?.given}`
          : ''
      }));
    }
  }, [patientDetails]);

  const handleDiagnosisCodeChange = (diagnosis: LabelAndValue | null) => {
    if (!diagnosis) {
      setFormValues((prev) => ({
        ...prev,
        diagnosisOption: undefined
      }));
      setEditingField(null);
      return;
    }

    setFormValues((prev) => ({
      ...prev,
      diagnosisOption: diagnosis
    }));
  };

  const handleUpdateDiagnosisCode = async (
    diagnosis: LabelAndValue | null,
    diagnosisId?: string
  ) => {
    if (!diagnosis) {
      return;
    }

    const params = { diagnosisOption: diagnosis.value };

    setFormValues((prev) => ({
      ...prev,
      diagnosisOption: {
        label: diagnosis.label,
        value: diagnosis.value
      }
    }));
    setEditingField(null);
    setLoadingField('diagnosisOption');

    try {
      if (isNewDiagnosisModalEnabled && diagnosisId) {
        await updatePrimaryDiagnosis({ diagnosisId, patientId }).unwrap();
      } else {
        await updateFieldData(params, `Diagnosis Code`);
      }
    } catch (error) {
      console.error(`Failed to sync billing for ${diagnosis.value}:`, error);
    }
    setLoadingField(null);
  };

  const handleFieldUpdate = async (field: EditableField, value: string) => {
    setEditingField(null);
    setLoadingField(field);
    const params = { [field]: value };
    await updateFieldData(params, 'OAC');
    setLoadingField(null);
  };

  const updateFieldData = async (params: Record<string, any>, fieldLabel: string) => {
    try {
      await updatePatientDetails({
        patient: {
          id: patientId,
          ...(params as unknown as Partial<Patient>)
        }
      }).unwrap();

      notifications.show({
        title: 'Success',
        message: `${fieldLabel} updated successfully.`,
        color: 'green'
      });
    } catch (error) {
      console.error(`Failed to update ${fieldLabel}:`, error);
      notifications.show({
        title: 'Error',
        message: `Failed to update ${fieldLabel}.`,
        color: 'red'
      });
    }
  };

  const getCustomEditableComponent = (value: string) => {
    if (value === 'diagnosisOption') {
      return (
        <DiagnosisSelect
          name={'diagnosisOption'}
          category={'patient'}
          defaultValue={formValues.diagnosisOption}
          onChange={(value: OnChangeValue<LabelAndValue, false>) =>
            handleUpdateDiagnosisCode(value ?? null)
          }
        />
      );
    }
    if (value === 'oac') {
      return (
        <Dropdown
          options={oacOptions}
          value={oacOptions.find((opt) => opt.value === formValues.oac)}
          onChange={(val) => {
            if (val && !Array.isArray(val)) {
              const selectedValue = val.value;
              setFormValues((prev) => ({
                ...prev,
                oac: selectedValue
              }));
              handleFieldUpdate('oac', selectedValue);
              setEditingField(null);
            }
          }}
        />
      );
    }
    return null;
  };

  if (isClinicDataLoading || isPatientLoading) {
    return <SkeletonGrid height={80} columns={2} rows={1} />;
  }

  if (isPatientError) {
    return (
      <Flex justify="center" align="center" h="50px" direction="column">
        <Text fz="md" fw={700} c="gray.7">
          We hit a snag loading patient details
        </Text>
        <Text fz="sm" c="dimmed">
          Try refreshing the page, or reach out to support if this keeps happening.
        </Text>
      </Flex>
    );
  }

  return (
    <Grid>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <LabeledField
          label="PRIMARY DIAGNOSIS CODE"
          value={formValues.diagnosisOption?.label ?? ''}
          isEditable={isNewDiagnosisModalEnabled ? false : isEditing('diagnosisOption')}
          onEdit={() => handleEdit('diagnosisOption')}
          customEditableComponent={
            isNewDiagnosisModalEnabled ? undefined : getCustomEditableComponent('diagnosisOption')
          }
          onSubmit={() => setEditingField(null)}
          isLoading={isFieldLoading('diagnosisOption')}
          showEditIcon={!isPractitioner}
        />
      </Grid.Col>
      <Grid.Col span={{ base: 12, md: 6 }}>
        <LabeledField
          label="OAC STATUS"
          value={formValues.oac ?? ''}
          isEditable={isEditing('oac')}
          onEdit={() => handleEdit('oac')}
          onSubmit={(val) => handleFieldUpdate('oac', val)}
          customEditableComponent={getCustomEditableComponent('oac')}
          isLoading={isFieldLoading('oac')}
          showEditIcon={true}
        />

        <LabeledField
          label="OAC DATE UPDATED"
          value={
            formValues.oacUpdatedAt
              ? `${formValues.oacUpdatedAt}${
                  formValues.oacUpdatedByUser ? ` by ${formValues.oacUpdatedByUser}` : ''
                }`
              : '-'
          }
        />
      </Grid.Col>
      {isNewDiagnosisModalEnabled && (
        <DxCodeSelectionModal
          opened={isDxModalOpen}
          onClose={() => setIsDxModalOpen(false)}
          onSave={handleUpdateDiagnosisCode}
          patientId={patientId}
          onChange={handleDiagnosisCodeChange}
        />
      )}
    </Grid>
  );
};

export default MedicalInfoDetails;
